/**
 * One account, location or settings-section result in the header search
 * dropdown (desktop list and the phone overlay). Picking it opens the
 * matching settings dialog (useSettingsSearch → openSettings).
 */
import React from 'react';
import { Building2, MapPin, Settings } from 'lucide-react';
import { theme } from '@turn-connect/ui';
import type { SettingsSearchHit } from '../../hooks/useSettingsSearch';

const ICONS = { section: Settings, account: Building2, location: MapPin } as const;

export interface SettingsSearchRowProps {
  hit: SettingsSearchHit;
  highlighted?: boolean;
  compact?: boolean;
  divider?: boolean;
  onPick: () => void;
  onHover?: () => void;
}

export const SettingsSearchRow: React.FC<SettingsSearchRowProps> = ({
  hit,
  highlighted = false,
  compact = true,
  divider = false,
  onPick,
  onHover,
}) => {
  const Icon = ICONS[hit.kind];
  return (
    <div
      role="option"
      aria-selected={highlighted}
      data-testid={`header-search-${hit.kind}`}
      onClick={onPick}
      onMouseEnter={onHover}
      style={{
        padding: compact ? '12px 16px' : '16px',
        cursor: 'pointer',
        backgroundColor: highlighted ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))' : 'transparent',
        borderBottom: divider ? `1px solid ${theme.colors.border.default}` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <Icon size={compact ? 14 : 16} style={{ color: theme.colors.text.secondary, flexShrink: 0 }} aria-hidden="true" />
        <span style={{ fontSize: compact ? 13 : 15, fontWeight: 600, color: theme.colors.text.primary, whiteSpace: 'nowrap' }}>
          {hit.label}
        </span>
        <span
          style={{
            fontSize: compact ? 11 : 12,
            color: theme.colors.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {hit.detail}
        </span>
      </div>
    </div>
  );
};

export default SettingsSearchRow;
